import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Space, Row, Col, message, DatePicker, Spin } from "antd";
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
  PDFDownloadLink,
} from "@react-pdf/renderer";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import { getGroupStatementApi } from "../../../services/Group/GroupStatementApis";

interface IModalProps {
  show: boolean;
  group: any;
  showModal: Function;
}

const { RangePicker } = DatePicker;

const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#fff",
    padding: 20,
  },
  header: {
    marginBottom: 20,
    alignItems: "center",
  },
  logo: {
    width: 100,
    height: 100,
    marginBottom: 10,
  },
  heading: {
    fontSize: 20,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 20,
  },
  infoSection: {
    border: "1px solid #000",
    padding: 12,
    marginBottom: 16,
    fontSize: 10,
  },
  row: {
    flexDirection: "row",
    marginBottom: 5,
  },
  label: {
    fontWeight: "bold",
    width: "40%",
  },
  value: {
    width: "60%",
  },
  table: {
    border: "1px solid #000",
    marginBottom: 16,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f0f0f0",
    borderBottom: "1px solid #000",
    padding: 8,
    fontSize: 10,
    fontWeight: "bold",
  },
  tableRow: {
    flexDirection: "row",
    borderBottom: "1px solid #ddd",
    padding: 8,
    fontSize: 9,
  },
  tableCell: {
    flex: 1,
  },
  summary: {
    border: "1px solid #000",
    padding: 12,
    marginBottom: 16,
    fontSize: 11,
  },
  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 5,
  },
  summaryLabel: {
    fontWeight: "bold",
  },
  footer: {
    textAlign: "center",
    fontSize: 10,
    marginTop: 20,
  },
});

const GroupStatementModal: React.FC<IModalProps> = ({
  show,
  group,
  showModal,
}) => {
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf("month"),
    dayjs().endOf("month"),
  ]);
  const [statementData, setStatementData] = useState<any>({
    members: [],
    payments: [],
    summary: {
      totalMembers: 0,
      activeMembers: 0,
      totalPayments: 0,
      totalAmount: 0,
      pendingAmount: 0,
    },
  });

  const handleModal = () => {
    showModal();
  };

  const handleDownload = () => {
    message.success("Statement is downloading...");
    setTimeout(() => {
      handleModal();
    }, 100);
  };

  const fetchStatementData = async () => {
    setLoading(true);
    try {
      // Call the API to get group statement data
      const startDate = dateRange[0].format("YYYY-MM-DD");
      const endDate = dateRange[1].format("YYYY-MM-DD");
      
      const groupId = group.id;
      
      if (!groupId) {
        message.error("Cannot fetch statement: Group ID is missing");
        throw new Error("Group ID is missing");
      }
      
      const response = await getGroupStatementApi(groupId, startDate, endDate);
      
      if (response.data && response.data.success) {
        setStatementData(response.data.data);
      } else {
        // API endpoint not implemented yet or returned error
        console.warn("Statement API not ready, using mock data. Response:", response);
        throw new Error(response.data?.message || "Failed to fetch statement data");
      }
    } catch (error: any) {
      console.error("Error fetching statement data:", error);
      
      // Check if it's a 404 (endpoint not implemented)
      if (error.response?.status === 404) {
        message.warning("Statement endpoint not yet implemented. Showing sample data.");
      } else {
        message.warning(error.response?.data?.message || "Using sample data - API endpoint not ready");
      }
      
      // Fallback to mock data in case of error
      const mockData = {
        members: [
          { id: 1, name: "John Doe", membershipNumber: "M001", status: "Active", premium: 500, paid: 500 },
          { id: 2, name: "Jane Smith", membershipNumber: "M002", status: "Active", premium: 750, paid: 750 },
          { id: 3, name: "Bob Johnson", membershipNumber: "M003", status: "Active", premium: 600, paid: 400 },
        ],
        payments: [
          { id: 1, date: dayjs().subtract(2, "days"), member: "John Doe", type: "Premium", amount: 500, reference: "PAY001" },
          { id: 2, date: dayjs().subtract(5, "days"), member: "Jane Smith", type: "Premium", amount: 750, reference: "PAY002" },
          { id: 3, date: dayjs().subtract(3, "days"), member: "Bob Johnson", type: "Premium", amount: 400, reference: "PAY003" },
        ],
        summary: {
          totalMembers: 3,
          activeMembers: 3,
          totalPayments: 3,
          totalAmount: 1650,
          pendingAmount: 200,
        },
      };

      setStatementData(mockData);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      fetchStatementData();
    }
  }, [show, dateRange]);

  const MyDocument = () => (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          {group.CompanyLogo && (
            <Image style={styles.logo} src={group.CompanyLogo} cache={false} />
          )}
          <Text style={styles.heading}>Group Statement</Text>
          <Text style={{ fontSize: 12, textAlign: "center" }}>
            {dateRange[0].format("DD MMM YYYY")} - {dateRange[1].format("DD MMM YYYY")}
          </Text>
        </View>

        <View style={styles.infoSection}>
          <View style={styles.row}>
            <Text style={styles.label}>Group Name:</Text>
            <Text style={styles.value}>{group.GroupName}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Group Email:</Text>
            <Text style={styles.value}>{group.Email}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Contact Number:</Text>
            <Text style={styles.value}>{group.Cellphone}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Statement Generated:</Text>
            <Text style={styles.value}>{dayjs().format("DD MMM YYYY HH:mm")}</Text>
          </View>
        </View>

        <Text style={{ fontSize: 12, fontWeight: "bold", marginBottom: 10 }}>
          Payment Transactions
        </Text>
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableCell, { flex: 1.2 }]}>Date</Text>
            <Text style={[styles.tableCell, { flex: 1.5 }]}>Member</Text>
            <Text style={[styles.tableCell, { flex: 1 }]}>Type</Text>
            <Text style={[styles.tableCell, { flex: 1 }]}>Amount</Text>
            <Text style={[styles.tableCell, { flex: 1.2 }]}>Reference</Text>
          </View>
          {statementData.payments.map((payment: any) => (
            <View key={payment.id} style={styles.tableRow}>
              <Text style={[styles.tableCell, { flex: 1.2 }]}>
                {dayjs(payment.date).format("DD/MM/YYYY")}
              </Text>
              <Text style={[styles.tableCell, { flex: 1.5 }]}>{payment.member}</Text>
              <Text style={[styles.tableCell, { flex: 1 }]}>{payment.type}</Text>
              <Text style={[styles.tableCell, { flex: 1 }]}>R {payment.amount}</Text>
              <Text style={[styles.tableCell, { flex: 1.2 }]}>{payment.reference}</Text>
            </View>
          ))}
        </View>

        <Text style={{ fontSize: 12, fontWeight: "bold", marginBottom: 10 }}>
          Summary
        </Text>
        <View style={styles.summary}>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Total Members:</Text>
            <Text>{statementData.summary.totalMembers}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Active Members:</Text>
            <Text>{statementData.summary.activeMembers}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Total Payments:</Text>
            <Text>{statementData.summary.totalPayments}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Total Amount Paid:</Text>
            <Text>R {statementData.summary.totalAmount.toFixed(2)}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Pending Amount:</Text>
            <Text>R {statementData.summary.pendingAmount.toFixed(2)}</Text>
          </View>
        </View>

        <View style={styles.footer}>
          <Text>Thank you for your continued partnership.</Text>
          <Text style={{ marginTop: 5 }}>
            For any queries, please contact us at {group.Email}
          </Text>
        </View>
      </Page>
    </Document>
  );

  return (
    <Modal
      title="Group Statement"
      open={show}
      onCancel={handleModal}
      width={1000}
      footer={null}
    >
      <div style={{ marginBottom: 20 }}>
        <Form.Item label="Select Date Range">
          <RangePicker
            value={dateRange}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                setDateRange([dates[0], dates[1]]);
              }
            }}
            format="DD MMM YYYY"
            style={{ width: "100%" }}
          />
        </Form.Item>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Spin size="large" />
          <p>Loading statement data...</p>
        </div>
      ) : (
        <>
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            {group.CompanyLogo && (
              <img src={group.CompanyLogo} alt="Logo" width="150px" />
            )}
            <h2>Group Statement</h2>
            <p>
              {dateRange[0].format("DD MMM YYYY")} - {dateRange[1].format("DD MMM YYYY")}
            </p>
          </div>

          <Row
            style={{
              border: "1px solid #d9d9d9",
              padding: "16px",
              marginBottom: "20px",
              borderRadius: "4px",
            }}
          >
            <Col span={12}>
              <p><strong>Group Name:</strong> {group.GroupName}</p>
              <p><strong>Email:</strong> {group.Email}</p>
            </Col>
            <Col span={12}>
              <p><strong>Contact:</strong> {group.Cellphone}</p>
              <p><strong>Generated:</strong> {dayjs().format("DD MMM YYYY HH:mm")}</p>
            </Col>
          </Row>

          <h3>Payment Transactions</h3>
          <div
            style={{
              border: "1px solid #d9d9d9",
              marginBottom: "20px",
              borderRadius: "4px",
              overflow: "hidden",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ backgroundColor: "#fafafa", borderBottom: "1px solid #d9d9d9" }}>
                  <th style={{ padding: "12px", textAlign: "left" }}>Date</th>
                  <th style={{ padding: "12px", textAlign: "left" }}>Member</th>
                  <th style={{ padding: "12px", textAlign: "left" }}>Type</th>
                  <th style={{ padding: "12px", textAlign: "right" }}>Amount</th>
                  <th style={{ padding: "12px", textAlign: "left" }}>Reference</th>
                </tr>
              </thead>
              <tbody>
                {statementData.payments.map((payment: any) => (
                  <tr key={payment.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td style={{ padding: "12px" }}>
                      {dayjs(payment.date).format("DD/MM/YYYY")}
                    </td>
                    <td style={{ padding: "12px" }}>{payment.member}</td>
                    <td style={{ padding: "12px" }}>{payment.type}</td>
                    <td style={{ padding: "12px", textAlign: "right" }}>
                      R {payment.amount.toFixed(2)}
                    </td>
                    <td style={{ padding: "12px" }}>{payment.reference}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3>Summary</h3>
          <Row
            style={{
              border: "1px solid #d9d9d9",
              padding: "16px",
              marginBottom: "20px",
              borderRadius: "4px",
              backgroundColor: "#fafafa",
            }}
          >
            <Col span={12}>
              <p><strong>Total Members:</strong> {statementData.summary.totalMembers}</p>
              <p><strong>Active Members:</strong> {statementData.summary.activeMembers}</p>
              <p><strong>Total Payments:</strong> {statementData.summary.totalPayments}</p>
            </Col>
            <Col span={12}>
              <p><strong>Total Amount Paid:</strong> R {statementData.summary.totalAmount.toFixed(2)}</p>
              <p><strong>Pending Amount:</strong> R {statementData.summary.pendingAmount.toFixed(2)}</p>
            </Col>
          </Row>

          <div style={{ textAlign: "center", marginTop: 30 }}>
            <Space>
              <Button onClick={handleModal}>Close</Button>
              <PDFDownloadLink
                document={<MyDocument />}
                fileName={`${group.GroupName}_Statement_${dateRange[0].format("YYYYMMDD")}-${dateRange[1].format("YYYYMMDD")}.pdf`}
              >
                <Button type="primary" onClick={handleDownload}>
                  Download PDF Statement
                </Button>
              </PDFDownloadLink>
            </Space>
          </div>
        </>
      )}
    </Modal>
  );
};

export default GroupStatementModal;
